<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .favorites-container
    //- Page Header
    .page-header
      h1.page-title Saved Events
      p.page-subtitle Your favorited events — save events to find them later

    //- Search Section
    .search-section
      .search-wrapper
        .search-input-container
          img(src="@/assets/icons/searchIcon.svg" alt="Search").search-icon
          input(
            v-model="state.searchTerm"
            placeholder="Search saved events..."
            @input="handleSearchInput"
            type="text"
          ).search-input
          button(
            v-if="state.searchTerm"
            @click="clearSearch"
            type="button"
          ).clear-button
            img(src="@/assets/icons/close.svg" alt="Clear")

    //- Loading State
    web-page-skeleton(v-if="state.isLoading && state.stickers.length === 0" :sections="1" :cardsPerSection="12")

    //- Content
    .content-section(v-if="!state.isLoading || state.stickers.length > 0")
      stickers-grid-template(:stickers="state.filteredStickers")

    //- Empty State
    .empty-state(v-if="!state.isLoading && state.filteredStickers.length === 0")
      .empty-state-icon ❤️
      h3.empty-state-title {{ state.searchTerm ? 'No events found' : 'No saved events yet' }}
      p.empty-state-text(v-if="state.searchTerm") Try adjusting your search terms
      p.empty-state-text(v-else) Start saving events to quickly find them later
      button(v-if="state.searchTerm" @click="clearSearch" type="button").clear-search-button Clear Search
</template>

<script lang="ts">
import {
  defineComponent, onMounted, reactive,
} from 'vue';
import { useRouter } from 'vue-router';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import WebPageSkeleton from '../components/WebPageSkeleton.vue';
import StickersGridTemplate from './StickersGridTemplate.vue';
import gqlRequest from '../dashboard/gql-request';

export default defineComponent({
  name: 'MyFavorites',
  components: { WebAppLayout, WebPageSkeleton, StickersGridTemplate },
  setup() {
    const {
      router, loading, getMyFavorites,
    } = gqlRequest();

    const state = reactive({
      stickers: [] as any[],
      filteredStickers: [] as any[],
      searchTerm: '',
      isLoading: false,
    });

    // Debounce timer
    let searchDebounceTimer: number | null = null;

    function handleSearchInput() {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }

      searchDebounceTimer = setTimeout(() => {
        filterStickers();
      }, 500);
    }

    function filterStickers() {
      if (!state.searchTerm) {
        state.filteredStickers = state.stickers;
        return;
      }

      const query = state.searchTerm.toLowerCase();
      state.filteredStickers = state.stickers.filter((sticker: any) => {
        const title = sticker.title?.toLowerCase() || '';
        const creatorName = sticker.organization?.name?.toLowerCase() || 
                           `${sticker.user?.firstname} ${sticker.user?.lastname}`.toLowerCase() || '';
        return title.includes(query) || creatorName.includes(query);
      });
    }

    async function getFavorites() {
      state.isLoading = true;
      loading.value = true;

      try {
        const result = await getMyFavorites();
        state.stickers = result || [];
        state.filteredStickers = state.stickers;
      } catch (error) {
        console.error('Failed to load favorites:', error);
        state.stickers = [];
        state.filteredStickers = [];
      } finally {
        state.isLoading = false;
        loading.value = false;
      }
    }

    function clearSearch() {
      state.searchTerm = '';
      filterStickers();
    }

    onMounted(async () => {
      await getFavorites();
    });

    return {
      router,
      loading,
      state,
      handleSearchInput,
      clearSearch,
    };
  },
});
</script>

<style scoped lang="scss">
.favorites-container {
  padding: 0 !important;
  min-height: 100vh;
  background: #ffffff;
}

.page-header {
  padding: 40px 24px 32px 24px;
  border-bottom: 1px solid #e5e7eb;
  
  @media (min-width: 768px) {
    padding: 48px 40px 40px 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 56px 64px 48px 64px;
  }
}

.page-title {
  font-size: 32px;
  font-weight: 900;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  line-height: 1.2;
  
  @media (min-width: 768px) {
    font-size: 40px;
  }
}

.page-subtitle {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
  
  @media (min-width: 768px) {
    font-size: 18px;
  }
}

.search-section {
  padding: 24px 24px 0 24px;
  margin-bottom: 24px;
  
  @media (min-width: 768px) {
    padding: 32px 40px 0 40px;
    margin-bottom: 32px;
  }
  
  @media (min-width: 1024px) {
    padding: 40px 64px 0 64px;
  }
}

.search-wrapper {
  display: flex;
  gap: 12px;
  align-items: center;
  max-width: 100%;
  
  @media (min-width: 768px) {
    gap: 16px;
  }
}

.search-input-container {
  position: relative;
  flex: 1;
  max-width: 600px;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  opacity: 0.4;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 14px 48px 14px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  color: #1a1a1a;
  background: #ffffff;
  transition: all 0.2s;
  
  &::placeholder {
    color: #9ca3af;
  }
  
  &:focus {
    outline: none;
    border-color: #0d9488;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }
}

.clear-button {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s;
  
  img {
    width: 16px;
    height: 16px;
    opacity: 0.5;
  }
  
  &:hover {
    background: #f3f4f6;
    
    img {
      opacity: 0.8;
    }
  }
}

.content-section {
  padding: 0 24px;
  margin-bottom: 40px;
  
  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 48px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 56px;
  }
}

.empty-state {
  padding: 80px 24px;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 100px 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 120px 64px;
  }
}

.empty-state-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.empty-state-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.empty-state-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.clear-search-button {
  padding: 12px 24px;
  background: #0d9488;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #0f766e;
    transform: scale(1.02);
  }
}

@media screen and (max-width: 500px) {
  .page-header {
    padding: 32px 24px 24px 24px;
  }
  
  .page-title {
    font-size: 28px;
  }
  
  .page-subtitle {
    font-size: 14px;
  }
  
  .search-input-container {
    max-width: 100%;
  }
}
</style>
